import React from 'react';
import { Animated, Text, TouchableOpacity, View } from 'react-native';
import { CloseButton } from '@/components/close-button';
import { createStyles } from '@/contexts/theme-context';
import { FilterType } from '@/components/filter-button';
import { FONTS, shadow, TYPOGRAPHY } from '@/styles';
import {
  HeaderListOption,
  useItemDetailTitleAnimation,
} from '@/utils/item-details';
import {
  HorizontalSelector,
  LIST_HEIGHT,
} from '@/components/horizontal-selector';
import { useScrollViewTabsContext } from '@/contexts/scroll-view-tabs-context';

interface Props {
  hasRichInfo: boolean;
  isVisible: boolean;
  ItemMenuButton: React.ReactNode;
  onClose: () => void;
  onTitlePress: () => void;
  title: string;
}

const BASE_VERTICAL_PADDING = 16;

export const ItemDetailTitleLarge: React.FC<Props> = ({
  hasRichInfo,
  isVisible,
  ItemMenuButton,
  onClose,
  onTitlePress,
  title,
}) => {
  const [styles] = useStyles();
  const { tabOptions, selectedTabOption, onTabPress } =
    useScrollViewTabsContext();

  const { headerOpacityRef, headerTranslateYRef } =
    useItemDetailTitleAnimation(isVisible);

  const renderContent = isVisible && hasRichInfo;

  return (
    <View style={[styles.base, renderContent ? styles.headerShadow : null]}>
      <Animated.View
        style={[
          styles.headerLeft,
          hasRichInfo
            ? {
                opacity: headerOpacityRef.current,
                transform: [{ translateY: headerTranslateYRef.current }],
              }
            : null,
        ]}
      >
        {renderContent ? (
          <>
            <TouchableOpacity onPress={onTitlePress} style={styles.titleButton}>
              <Text
                numberOfLines={2}
                style={[TYPOGRAPHY.medium, FONTS.primaryBold]}
              >
                {title}
              </Text>
            </TouchableOpacity>
            <HorizontalSelector<HeaderListOption>
              contentContainerStyle={styles.selectorContainer}
              data={tabOptions}
              filterType={FilterType.TAB}
              hideFooterSpacing
              keyExtractor={(item) => String(item.value)}
              onItemPress={onTabPress}
              scrollToItem
              selectedItem={selectedTabOption}
              trackingKey="item-detail-sections"
            />
          </>
        ) : null}
      </Animated.View>
      {ItemMenuButton}
      <CloseButton
        onPress={onClose}
        testID="modal-close-button"
        trackingData={null}
      />
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    backgroundColor: theme.colors.white,
    flexDirection: 'row',
    height: LIST_HEIGHT + BASE_VERTICAL_PADDING,
    justifyContent: 'space-between',
    paddingHorizontal: 16,
  },
  headerLeft: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
  },
  headerShadow: shadow({
    elevation: 0,
    opacity: 0.1,
    radius: 4,
    x: 0,
    y: 4,
  }),
  selectorContainer: {
    height: LIST_HEIGHT + BASE_VERTICAL_PADDING,
  },
  titleButton: {
    maxWidth: '33%',
    paddingRight: 8,
    paddingVertical: 8,
    textAlign: 'center',
  },
}));
